import { v4 as uuidv4 } from 'uuid';

declare const reviewItemIdBrand: unique symbol;
declare const quizEditionItemUuidBrand: unique symbol;
declare const quizUuidBrand: unique symbol;

/**
 * Client-side Review Editor row identity.
 *
 * Prefixed values (`db-`, `manual-`, `placeholder-`) are intentional — they are
 * not interchangeable with database UUIDs passed to Supabase RPCs.
 */
export type ReviewItemId = string & { readonly [reviewItemIdBrand]: 'ReviewItemId' };

/** `public.quiz_edition_items.id` */
export type QuizEditionItemUuid = string & {
  readonly [quizEditionItemUuidBrand]: 'QuizEditionItemUuid';
};

/** `public.quizzes.id` */
export type QuizUuid = string & { readonly [quizUuidBrand]: 'QuizUuid' };

export const QUIZ_EDITION_ITEM_ID_PREFIX = 'db-' as const;
export const MANUAL_REVIEW_ITEM_ID_PREFIX = 'manual-' as const;
export const PLACEHOLDER_REVIEW_ITEM_ID_PREFIX = 'placeholder-' as const;

function createPrefixedReviewItemId(prefix: string): ReviewItemId {
  return `${prefix}${uuidv4()}` as ReviewItemId;
}

/** Cast at an external boundary (Supabase row, JSON, test fixture). */
export function toReviewItemId(value: string): ReviewItemId {
  return value as ReviewItemId;
}

export function toQuizEditionItemUuid(value: string): QuizEditionItemUuid {
  return value as QuizEditionItemUuid;
}

export function toQuizUuid(value: string | null): QuizUuid | null {
  return value ? (value as QuizUuid) : null;
}

export function createManualReviewItemId(): ReviewItemId {
  return createPrefixedReviewItemId(MANUAL_REVIEW_ITEM_ID_PREFIX);
}

export function createPlaceholderReviewItemId(): ReviewItemId {
  return createPrefixedReviewItemId(PLACEHOLDER_REVIEW_ITEM_ID_PREFIX);
}

/** Wrap a persisted quiz edition item row id for Review Editor state. */
export function toPersistedReviewItemId(
  quizEditionItemUuid: QuizEditionItemUuid | string
): ReviewItemId {
  return toReviewItemId(`${QUIZ_EDITION_ITEM_ID_PREFIX}${quizEditionItemUuid}`);
}

/** Strip the client-side prefix from a persisted quiz edition item ReviewItem id. */
export function parseQuizEditionItemId(reviewItemId: ReviewItemId): QuizEditionItemUuid | null {
  if (!reviewItemId.startsWith(QUIZ_EDITION_ITEM_ID_PREFIX)) {
    return null;
  }

  const quizEditionItemId = reviewItemId.slice(QUIZ_EDITION_ITEM_ID_PREFIX.length);
  return quizEditionItemId.length > 0 ? toQuizEditionItemUuid(quizEditionItemId) : null;
}

type PartialPublishReviewItem = {
  id: ReviewItemId;
  selected: boolean;
};

/**
 * Map editor selection to database UUIDs after save.
 *
 * The save RPC replaces all rows, so IDs must be read from the
 * reloaded items (matched by index with the pre-save review items).
 */
export function resolvePartialPublishItemIds(
  reviewItems: PartialPublishReviewItem[],
  savedItems: PartialPublishReviewItem[]
): QuizEditionItemUuid[] {
  const length = Math.min(reviewItems.length, savedItems.length);
  const itemIds: QuizEditionItemUuid[] = [];

  for (let index = 0; index < length; index += 1) {
    if (!reviewItems[index]?.selected) {
      continue;
    }

    const quizEditionItemId = parseQuizEditionItemId(savedItems[index].id);
    if (quizEditionItemId) {
      itemIds.push(quizEditionItemId);
    }
  }

  return itemIds;
}
